import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';

@Injectable({
  providedIn: 'root'
})
export class PrecastTechWorksheetPdfService {

    generateTRWorksheet(worksheetRecords: any, worksheets: any[]): void {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        this.addTitlePage(
            doc,
            worksheetRecords,
            worksheets
        );

        const formattedDueDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.dueDate,'MMddyy');
        const fileNameParts = [
        worksheetRecords.company.name,
        worksheetRecords.project.projectName,
        worksheetRecords.project.projectNumber,
        DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.sampleReceivedDate, 'MM/dd/yy'),
        ]
        .filter(v => typeof v === 'string' && v.trim().length > 0)
        .map(v => this.sanitizeFileName(v));

        const fileName = fileNameParts.join('-');

        // ALWAYS force PDF extension
        doc.save(`${fileName}.pdf`);
    }

    sanitizeFileName(value: string): string {
        return value.replace(/[\/\\:*?"<>|$%]/g, '').replace(/\s+/g, ' ').trim().replace(/\.+$/, '');
    }

    private drawHeader(
        doc: jsPDF,
        worksheetRecords: any,
        margin: number,
        usableWidth: number,
    ): number {

        const formattedSampleReceivedDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.sampleReceivedDate, 'MM/dd/yyyy');
        const formattedDueDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.dueDate, 'MM/dd/yyyy');
        const testingInformation = worksheetRecords.comments
            .filter((x: { projectComment: any }) => x.projectComment?.trim())
            .map((x: { sampleTypeName: any; projectComment: any }) =>
                `${x.sampleTypeName} : ${x.projectComment}`
            )
            .join(' | ');

        autoTable(doc, {
        startY: 5,
        theme: 'grid',
        margin: { left: margin, right: margin },
        tableWidth: usableWidth,

        columnStyles: {
            0: { cellWidth: usableWidth / 100 * 9 },
            1: { cellWidth: usableWidth / 100 * 18.5, fontStyle: 'bold' },
            2: { cellWidth: usableWidth / 100 * 12.5 },
            3: { cellWidth: usableWidth / 100 * 8, fontStyle: 'bold' },
            4: { cellWidth: usableWidth / 100 * 10 },
            5: { cellWidth: usableWidth / 100 * 22, fontStyle: 'bold' },
            6: { cellWidth: usableWidth / 100 * 10 },
            7: { cellWidth: usableWidth / 100 * 9, fontStyle: 'bold' }
        },

        styles: {
            fontSize: 9,
            textColor: 0,
            valign: 'middle',
            halign: 'left',
            overflow: 'linebreak',
            cellPadding: 1,
            lineColor: 0,
            lineWidth: 0.1
        },

        body: [
            [
                { content: 'Manager Notes', colSpan: 1, styles: { minCellHeight: 7 } },
                { content: '', colSpan: 5, styles: { minCellHeight: 7 } },
                { 
                    content: 'Pre Cast', 
                    colSpan: 2,             
                    styles: {
                        halign: 'center',
                        minCellHeight: 7,
                        fontStyle: 'bold',
                        fontSize: 11,
                    } 
                },
            ],
            [
                'Proj. Reference', worksheetRecords.project.referenceId,
                'Sample Received Date', formattedSampleReceivedDate || '',
                'Company Name', worksheetRecords.company.name,
                '',
                ''
            ],
            [
                'Project Name', worksheetRecords.project.projectName,
                'Due Date', formattedDueDate || '',
                'Project Location', worksheetRecords.project.projectLocation,
                '', 
                '',
            ],
            [
                'Project Number', worksheetRecords.project.projectNumber,
                'Oven Date', worksheetRecords.project.ovenDate || '',
                'Pre Approval', worksheetRecords.project.preApproval === true ? 'YES' : 'NO',
                'Storage Location', worksheetRecords.project.storageCompartment || '',
            ],
            [
                { content: 'Wet Test Data', colSpan: 1, styles: { minCellHeight: 7 } },
                { content: '', colSpan: 7, styles: { minCellHeight: 7 } },
            ],
            [
                { content: 'Dry Test Data', colSpan: 1, styles: { minCellHeight: 7 } },
                { content: '', colSpan: 5, styles: { minCellHeight: 7 } },
                {
                    content: worksheetRecords.project.rushTesting ? 'RUSH' : '',
                    colSpan: 2,
                    styles: {
                    halign: 'center',
                    minCellHeight: 7,
                    fontStyle: 'bold',
                    fontSize: 11,
                    }
                }
            ],
            [
                { content: 'Testing Info', colSpan: 1, styles: { minCellHeight: 5 } },
                { content: testingInformation, colSpan: 7, styles: { minCellHeight: 5 } },
            ],
        ]
        });

        return (doc as any).lastAutoTable.finalY;
    }

    private addTitlePage(
        doc: jsPDF,
        worksheetRecords: any,
        worksheets: any[]
    ): void {

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 5;
        const usableWidth = pageWidth - margin * 2;

        // DRAW HEADER FIRST TIME
        const headerFinalY = this.drawHeader(
        doc,
        worksheetRecords,
        margin,
        usableWidth,
        );

        // START POSITION FOR MAIN TABLE
        const finalY = headerFinalY + 4;

        // WORKSHEET ROWS
        const worksheetRows: any[] = [];

        for (let i = 0; i < worksheets.length; i++) {
        const item = worksheets[i];
        const formattedCastDate = DateTimeUtils.formatDateAndTime(item.castDate, 'MM/dd/yy');

        const hasValue = (v: any) =>
            v !== null &&
            v !== undefined &&
            v !== '';

        const depthRange =
            hasValue(item.depth) && hasValue(item.endDepth)
            ? `${item.depth}-${item.endDepth}`
            : hasValue(item.depth)
                ? item.depth
                : hasValue(item.endDepth)
                ? item.endDepth
                : '';

        let rowSpanCount = 1;

        for (let j = i + 1; j < worksheets.length; j++) {
            if (
            worksheets[j].preCastSampleId === item.preCastSampleId
            ) {
            rowSpanCount++;
            } else {
            break;
            }
        }

        const row: any[] = [
            { content: item.inHouseId, styles: { fontSize: 9 } },
            rowSpanCount > 1
            ? {
                content: item.sampleId || '',
                rowSpan: rowSpanCount,
                styles: {
                    valign: 'middle',
                    halign: 'center'
                }
                }
            : item.sampleId || '',
            rowSpanCount > 1
            ? {
                content: formattedCastDate || '',
                rowSpan: rowSpanCount,
                styles: {
                    valign: 'middle',
                    halign: 'center'
                }
                }
            : formattedCastDate || '',
            rowSpanCount > 1
            ? {
                content: item.sampleType || '',
                rowSpan: rowSpanCount,
                styles: {
                    valign: 'middle',
                    halign: 'center'
                }
                }
            : item.sampleType || '',
            rowSpanCount > 1
            ? {
                content: item.mixDesignCode || '',
                rowSpan: rowSpanCount,
                styles: {
                    valign: 'middle',
                    halign: 'center'
                }
                }
            : item.mixDesignCode || '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            ''
        ];

        worksheetRows.push(row);

        // DUPLICATE ROWS
        for (let k = 1; k < rowSpanCount; k++) {
            const nextItem = worksheets[i + k];      
            worksheetRows.push([
            { 
                content: nextItem.inHouseId, styles: { fontSize: 9 } },
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
            ]);
        }

        i += rowSpanCount - 1;
        }

        // MAIN TABLE
        autoTable(doc, {
            startY: finalY,
            theme: 'grid',
            margin: {
                left: margin,
                right: margin,
            },

            tableWidth: usableWidth,
            head: [
                [
                {
                    content: 'In\nHouse\nID',
                    rowSpan: 2,
                },
                {
                    content: 'Sample\nID',
                    rowSpan: 2
                },
                {
                    content: 'Cast\nDate',
                    rowSpan: 2
                },
                {
                    content: 'Sample\nType',
                    rowSpan: 2
                },
                {
                    content: 'Mix ID',
                    rowSpan: 2
                },
                {
                    content: 'Tare\n(gm)',
                    rowSpan: 2
                },
                {
                    content: 'Mould\nWeight\n(gm)',
                    rowSpan: 2
                },
                {
                    content: 'Sample\nDiameter\n(mm)',
                    rowSpan: 2
                },
                {
                    content: 'Sample\nHeight\n(mm)',
                    rowSpan: 2
                },
                {
                    content: 'Test 1',
                    colSpan: 2,
                    styles: { halign: 'center' }
                },
                {
                    content: 'Test 2',
                    colSpan: 2,
                    styles: { halign: 'center' }
                },
                {
                    content: 'Test 3',
                    colSpan: 2,
                    styles: { halign: 'center' }
                },
                {
                    content: 'Dry Test',
                    colSpan: 2,
                    styles: { halign: 'center' }
                },
                {
                    content: 'Note',
                    rowSpan: 2
                },
                ],

                [
                    'Weight',
                    'TR',
                    'Weight',
                    'TR',
                    'Weight',
                    'TR',
                    'Weight',
                    'TR',
                    'Dry Weight',
                    'Dry TR'
                ]
            ],

            didParseCell: (data) => {
                if (data.section !== 'body') {
                    return;
                }

                const currentIndex = data.row.index;
                const currentItem = worksheets[currentIndex];
                const nextItem = worksheets[currentIndex + 1];
                const isLastMergedRow = !nextItem || currentItem.preCastSampleId !== nextItem.preCastSampleId;

                // NORMAL ROW CELLS
                if (isLastMergedRow) {

                    Object.values(data.row.cells).forEach((cell: any) => {
                        cell.styles.lineWidth = {
                            top: 0.1,
                            right: 0.1,
                            bottom: 0.5,
                            left: 0.1
                        };
                    });
                }

                // ROWSPAN CELLS
                if (data.cell.rowSpan && data.cell.rowSpan > 1) {
                    data.cell.styles.lineWidth = {
                    top: 0.1,
                    right: 0.1,
                    bottom: 0.5,
                    left: 0.1
                    };
                }
            },

            body: worksheetRows,

            styles: {
                fontSize: 8,
                textColor: 0,
                lineColor: 0,
                lineWidth: 0.1,
                cellPadding: 1,
                minCellHeight: 9,
                valign: 'middle',
                halign: 'center',
            },

            headStyles: {
                fillColor: [220, 220, 220],
                textColor: 0,
                fontStyle: 'bold',
                valign: 'middle',
                halign: 'center'
            },

            columnStyles: {
                0: { cellWidth: usableWidth / 100 * 5 },
                1: { cellWidth: usableWidth / 100 * 6 },
                2: { cellWidth: usableWidth / 100 * 5 },
                3: { cellWidth: usableWidth / 100 * 6 },
                4: { cellWidth: usableWidth / 100 * 6 },

                5: { cellWidth: usableWidth / 100 * 5 },
                6: { cellWidth: usableWidth / 100 * 5 },
                7: { cellWidth: usableWidth / 100 * 5 },
                8: { cellWidth: usableWidth / 100 * 5 },

                // TEST #1
                9: { cellWidth: usableWidth / 100 * 6 },
                10: { cellWidth: usableWidth / 100 * 5 },

                // TEST #2
                11: { cellWidth: usableWidth / 100 * 6 },
                12: { cellWidth: usableWidth / 100 * 5 },

                // TEST #3
                13: { cellWidth: usableWidth / 100 * 6 },
                14: { cellWidth: usableWidth / 100 * 5 },

                // DRY TEST
                15: { cellWidth: usableWidth / 100 * 6 },
                16: { cellWidth: usableWidth / 100 * 5 },

                17: { cellWidth: usableWidth / 100 * 7 },
            }
        });
    }
}